// Wohler Chemical — Tryptamine Analogs (Program B)
// Graduate-school level research content

export const tryptamines = {
  "4-ho-met": {
    id: "4-ho-met",
    name: "4-HO-MET",
    fullName: "4-Hydroxy-N-methyl-N-ethyltryptamine",
    aliases: ["Metocin", "Homopsilocin"],
    categoryId: "tryptamines",
    program: "Program B",
    cas: "77872-43-6",
    formula: "C₁₃H₁₈N₂O",
    molecularWeight: "218.30 g/mol",
    schedulingStatus: "unscheduled",
    schedulingNote: "Not federally scheduled (DEA) as of 2026. 4-OH-DiPT was federally scheduled in 2022; 4-HO-MET remains unscheduled but is structurally related to psilocin (Schedule I). Analog-Act consideration applies only to substances intended for human consumption — research use only is the required posture. Several U.S. states have independent scheduling laws; verify per-state.",
    benefitScore: 88,
    harmScore: 22,
    evidenceConfidence: "HIGH",
    status: "Strongly Favored",
    statusColor: "green",
    goDecision: "GO — strong university companion to 4-AcO-MET",
    summary: "4-HO-MET is the direct active metabolite of Wohler's flagship 4-AcO-MET and a close psilocin homolog. In head-twitch response (HTR) assays at UCSD (Klein/Halberstadt 2021) it is more potent than psilocin (ED50 = 0.65 vs 0.81 μmol/kg) and functions as a near-full agonist at 5-HT2A (Emax ≈ 97%). Shorter duration than psilocybin (~4–5 h) makes it attractive for structured therapeutic sessions.",
    pros: [
      "More potent than psilocin in the validated mouse HTR assay (ED50 = 0.65 μmol/kg vs 0.81 for psilocin)",
      "Near-full agonist at 5-HT2A (Emax ≈ 97%) — matches psilocybin-like therapeutic mechanism",
      "Shorter duration (~4–5 h) favorable for structured psychedelic-assisted therapy protocols",
      "Direct active metabolite of 4-AcO-MET — allows bypassing of prodrug conversion variability",
      "Strong published SAR data in ACS Pharmacology & Translational Science (2021), PMC8033608",
      "Logical university-grade companion compound for 4-AcO-MET research lines already in place"
    ],
    cons: [
      "No formal human clinical trials yet — most human data is observational (Kjellgren & Soussan 2011)",
      "5-HT2B efficacy (Emax ≈ 44%) requires cardiac valve monitoring in chronic dosing paradigms",
      "Structurally close to psilocin (Schedule I) — high scrutiny class, emergency scheduling risk",
      "Requires a DEA-registered or institution-based schedule-I-waiver environment for live-subject work"
    ],
    pharmacology: {
      targets: [
        { receptor: "5-HT2A", activity: "Full agonist", value: "EC₅₀ ≈ 10 nM; Emax ≈ 97%" },
        { receptor: "5-HT2B", activity: "Partial agonist", value: "Emax ≈ 44%" },
        { receptor: "5-HT2C", activity: "Partial agonist", value: "Emax ≈ 65%" },
        { receptor: "5-HT1A", activity: "Partial agonist (weak)", value: "Low affinity" }
      ],
      htrED50: "0.65 μmol/kg mouse (Klein et al. 2021)",
      duration: "4–5 h oral (vs 6–8 h for psilocybin)",
      halfLife: "Estimated 1–2 h (analogous to psilocin; no formal human PK data)",
      metabolism: "Glucuronidation of 4-OH via UGT1A10/UGT1A9; N-demethylation via CYP2D6 (minor)",
      mechanism: "Serotonergic psychedelic — 5-HT2A agonism drives glutamate release in layer-5 pyramidal neurons, promoting BDNF/mTORC1-dependent synaptic plasticity"
    },
    labSynthesis: {
      title: "Laboratory-Scale Synthesis (10–50 g)",
      overview: "Repke synthesis (1977/1981) — Speeter-Anthony tryptamine approach starting from 4-acetoxyindole. Final O-deacetylation delivers 4-HO-MET.",
      steps: [
        {
          step: "1",
          title: "Glyoxalyl chloride formation",
          description: "4-Acetoxyindole (1.0 eq) in dry THF, cooled to 0 °C; add oxalyl chloride (1.1 eq) dropwise; stir 30 min → 4-acetoxy-indole-3-glyoxalyl chloride.",
          solvent: "THF (anhydrous)",
          keyReagents: "Oxalyl chloride, 4-acetoxyindole"
        },
        {
          step: "2",
          title: "Amide coupling",
          description: "Add N-methyl-N-ethylamine (2.5 eq) and triethylamine (3 eq) at 0 °C; warm to RT, stir 2 h → 4-acetoxy-indole-3-glyoxalyl-(N-methyl-N-ethyl)amide.",
          solvent: "THF",
          keyReagents: "N-methyl-N-ethylamine, Et₃N"
        },
        {
          step: "3",
          title: "Reduction to tryptamine",
          description: "Dissolve amide in anhydrous THF; add LAH (4 eq) portionwise under N₂; reflux 4 h. Quench Fieser workup (H₂O/NaOH/H₂O). Filter, concentrate → 4-AcO-MET free base.",
          solvent: "THF",
          keyReagents: "LiAlH₄ (LAH)"
        },
        {
          step: "4",
          title: "O-deacetylation",
          description: "Dissolve 4-AcO-MET in MeOH; add K₂CO₃ (2 eq), stir RT 2 h. TLC monitoring. Concentrate, partition EtOAc/H₂O, dry over Na₂SO₄ → 4-HO-MET free base. Convert to fumarate salt (fumaric acid in EtOH) for storage.",
          solvent: "MeOH → EtOAc/H₂O → EtOH",
          keyReagents: "K₂CO₃, fumaric acid"
        }
      ],
      yield: "42–55 % over 4 steps",
      purityTarget: "≥ 99.0 % by HPLC (area %); single enantiomer N/A (achiral)"
    },
    kgScaleSynthesis: {
      title: "Kilogram-Scale Manufacturing Plan",
      feasibility: "Moderate — all steps compatible with Wohler's standard solvent inventory.",
      solventsUsed: ["THF", "Ethyl Acetate", "Methanol", "Ethanol", "Heptane (for final crystallization)"],
      equipment: [
        "50–200 L jacketed glass-lined reactor w/ N₂ inerting",
        "Acid-gas scrubber (NaOH) for step 1 HCl evolution",
        "Dry LAH addition port + emergency isolation valve",
        "Controlled cryogenic feed (−5 °C to 0 °C) for step 1",
        "Rotary evaporator ≥ 20 L capacity",
        "Büchner nutsche filter + drying oven"
      ],
      batchPlan: {
        targetBatch: "1.0 kg 4-HO-MET fumarate",
        startingMaterial: "2.1 kg 4-acetoxyindole (C₁₀H₉NO₃, 175 g/mol; ≈ 12 mol)",
        keyVolumes: [
          "THF: 15 L (step 1 + 2)",
          "Oxalyl chloride: 1.15 kg (1.1 eq)",
          "N-methyl-N-ethylamine: 1.75 kg (2.5 eq, solution)",
          "LAH: 1.8 kg (4 eq) — slurried in THF under N₂",
          "MeOH for deacetylation: 12 L",
          "EtOAc/H₂O workup: 20 L each",
          "Fumaric acid: 0.55 eq in EtOH for salt formation"
        ]
      },
      hazards: [
        "LAH pyrophoric — dedicated LAH-rated reactor, N₂ blanket, explosion-vented cell",
        "Oxalyl chloride evolves HCl + CO on hydrolysis — mandatory scrubber + CO monitor",
        "THF peroxide check before each batch — reject lots > 100 ppm peroxide",
        "Heated reflux of THF — local vent exhaust, spark-free motors only (ATEX zone 1)"
      ],
      vendorNotes: [
        "4-Acetoxyindole: Combi-Blocks, AmBeed, Matrix Scientific",
        "Oxalyl chloride: Sigma-Aldrich, Thermo Scientific",
        "LAH: Albemarle (bulk pellet), Sigma-Aldrich",
        "Fumaric acid (USP): Bartek Ingredients",
        "Reactor time estimate: 5 days per 1 kg batch including workup, drying, QC"
      ],
      estCostPerKg: "$18,500 – $24,000 (raw materials + utilities, pre-QC)"
    },
    safety: {
      acuteToxicity: "No formal LD50 published. Mouse HTR active at 0.65 μmol/kg IP. Recreational human doses 10–30 mg oral — no fatalities reported in published literature at recreational doses.",
      chronicConcerns: [
        "5-HT2B partial agonism → cardiac valvulopathy risk with chronic/repeated dosing (class effect)",
        "Serotonin syndrome potential if co-administered with MAOIs, SSRIs, or other serotonergic agents",
        "Vasoconstriction & transient hypertension during peak effects"
      ],
      contraindications: [
        "Personal/family history of psychotic disorders (schizophrenia, bipolar I)",
        "Structural cardiac disease or untreated hypertension",
        "Current MAOI therapy (14-day washout required)",
        "Pregnancy and lactation — no data; exclusionary"
      ],
      acuteSideEffects: [
        "Visual alteration — geometric patterns, enhanced color",
        "Mood amplification — euphoria, anxiety depending on set/setting",
        "Tachycardia (typical 90–115 bpm at peak)",
        "Mydriasis, mild hyperthermia",
        "Nausea (particularly during onset phase, ~20 min)"
      ],
      ehsClassification: "GHS: suspected acute toxicity Cat. 4 (oral); eye/skin irritant Cat. 2. Handle in fume hood, nitrile gloves, safety goggles, lab coat. No respirator required for solid-phase handling ≤ 10 g."
    },
    publishedResearch: [
      { citation: "Klein AK, Chatha M, Laskowski LJ, Anderson EI, Brandt SD, Chapman SJ, McCorvy JD, Halberstadt AL. Investigation of the Structure–Activity Relationships of Psilocybin Analogues. ACS Pharmacol Transl Sci. 2021 Apr 16;4(2):533-542.", pmid: "33860181", pmc: "PMC8033608", url: "https://pmc.ncbi.nlm.nih.gov/articles/PMC8033608/" },
      { citation: "Kjellgren A, Soussan C. Heaven and hell — a phenomenological study of recreational use of 4-HO-MET in Sweden. J Psychoactive Drugs. 2011;43(3):211-9.", pmid: "22111404", url: "https://pubmed.ncbi.nlm.nih.gov/22111404/" },
      { citation: "Repke DB, Ferguson WJ, Bates DK. Psilocin analogs. J Heterocycl Chem. 1977;14:71-74." },
      { citation: "Repke DB, Ferguson WJ. Psilocin analogs II. J Heterocycl Chem. 1981;18:175-179." }
    ],
    patentInfo: "No active composition-of-matter patents on 4-HO-MET itself. Original Hofmann patent family (1960s) long expired. Opportunity space exists for (a) purification-method patents, (b) fumarate/crystal-form patents, (c) formulation patents (e.g., sublingual, intranasal), (d) method-of-use patents for specific clinical indications.",
    wohlerScoring: {
      methodology: "Wohler Scoring System v1.0 — Benefit (0-100) weights therapeutic rationale, evidence quality, novelty. Harm (0-100) weights acute + chronic risks, dependence, scheduling trajectory. Net Research Value = Benefit − Harm. Confidence reflects evidence base.",
      benefitBreakdown: {
        therapeuticRationale: 22,
        evidenceQuality: 20,
        unmetMedicalNeed: 18,
        novelty: 14,
        universityInterest: 14
      },
      harmBreakdown: {
        acuteToxicity: 5,
        chronicToxicity: 6,
        dependence: 3,
        schedulingRisk: 8
      },
      netValue: 66,
      confidenceRationale: "HIGH — multiple peer-reviewed SAR studies; direct metabolite of an existing Wohler product; well-characterized receptor pharmacology."
    },
    priorityTier: "Tier 1 — Immediate synthesis target",
    nextSteps: [
      "Finalize fumarate crystal form; perform single-crystal XRD for patent disclosure",
      "Generate 100-mg reference standard for CRO distribution with full COA",
      "Draft IRB-ready investigator brochure for university partners",
      "Commission 14-day rat tox at 3 mg/kg/day (behavioral + cardiac endpoints)"
    ]
  },

  "4-aco-det": {
    id: "4-aco-det",
    name: "4-AcO-DET",
    fullName: "4-Acetoxy-N,N-diethyltryptamine",
    aliases: ["Ethacetin", "Psilacetin-DET", "CZ-74 prodrug"],
    categoryId: "tryptamines",
    program: "Program B",
    cas: "936-04-9 (free base)",
    formula: "C₁₆H₂₂N₂O₂",
    molecularWeight: "274.36 g/mol",
    schedulingStatus: "unscheduled",
    schedulingNote: "Not federally scheduled. Active metabolite (4-HO-DET / CZ-74) was studied in humans by Hofmann and Leuner in 1960s Swiss psychiatric protocols. Structurally related to scheduled psilocin; analog considerations identical to 4-HO-MET.",
    benefitScore: 85,
    harmScore: 24,
    evidenceConfidence: "HIGH",
    status: "Strongly Favored",
    statusColor: "green",
    goDecision: "GO — 60+ years of human data via CZ-74",
    summary: "O-acetyl prodrug of 4-HO-DET. The N,N-diethyl analog of 4-AcO-DMT has one of the longest human safety records among tryptamines — it was studied as CZ-74 / CEY-19 by Leuner & Baer in 1960s Swiss clinical psychotherapy trials. Shorter duration than psilocybin; lower 5-HT2C activation than psilocin.",
    pros: [
      "Active metabolite CZ-74 has 60+ years of documented human clinical experience (Leuner, Baer, Hofmann)",
      "Prodrug mechanism identical to 4-AcO-MET — esterase-driven O-deacetylation in plasma",
      "ED50 = 0.71 mg/kg in mouse HTR (Klein et al. 2021) — robust in vivo activity",
      "Lower 5-HT2C component than psilocin — potentially cleaner side-effect profile",
      "Shorter duration than psilocybin — operationally attractive for 4-hour therapy sessions"
    ],
    cons: [
      "5-HT2B Emax ≈ 64 % — moderate cardiac valve concern with chronic dosing",
      "Most clinical data is 1960s — modern PK/PD reanalysis required",
      "Psilocin scaffold — scheduling scrutiny applies"
    ],
    pharmacology: {
      targets: [
        { receptor: "5-HT2A", activity: "Partial-to-full agonist", value: "EC₅₀ ≈ 184 nM in vitro; Emax ≈ 92%" },
        { receptor: "5-HT2B", activity: "Agonist", value: "Emax ≈ 64 %" },
        { receptor: "5-HT2C", activity: "Partial agonist", value: "Lower than psilocin" },
        { receptor: "5-HT1A", activity: "Low-affinity partial agonist", value: "Minor contribution" }
      ],
      htrED50: "0.71 mg/kg mouse (Klein et al. 2021)",
      duration: "4–5 h oral",
      halfLife: "Prodrug converted to 4-HO-DET within minutes in plasma",
      metabolism: "Esterase cleavage → 4-HO-DET → glucuronidation",
      mechanism: "Classic 5-HT2A psychedelic neuroplasticity mechanism; CZ-74 literature shows durable antidepressant-like effects in open-label 1960s trials"
    },
    labSynthesis: {
      title: "Laboratory-Scale Synthesis",
      overview: "Two routes: (A) Speeter-Anthony from 4-acetoxyindole + diethylamine — identical protocol to 4-HO-MET but with N,N-diethylamine in step 2, and NO step-4 deacetylation; (B) direct O-acetylation of 4-HO-DET with acetic anhydride/pyridine.",
      steps: [
        { step: "1", title: "Glyoxalyl chloride formation", description: "4-Acetoxyindole (1.0 eq) + oxalyl chloride (1.1 eq) in THF, 0 °C, 30 min.", solvent: "THF", keyReagents: "Oxalyl chloride" },
        { step: "2", title: "Amidation with diethylamine", description: "Add diethylamine (2.5 eq) + Et₃N (3 eq) at 0 °C; stir RT 2 h.", solvent: "THF", keyReagents: "Et₂NH, Et₃N" },
        { step: "3", title: "LAH reduction", description: "LAH (4 eq) in THF, reflux 4 h; Fieser workup; isolate 4-AcO-DET free base.", solvent: "THF", keyReagents: "LiAlH₄" },
        { step: "4", title: "Fumarate crystallization", description: "Dissolve free base in hot EtOH; add 0.55 eq fumaric acid; cool to 0 °C; filter.", solvent: "Ethanol", keyReagents: "Fumaric acid" }
      ],
      yield: "48–60 % over 3 steps (no deacetylation needed)",
      purityTarget: "≥ 99.0 % HPLC"
    },
    kgScaleSynthesis: {
      title: "Kilogram-Scale Plan",
      feasibility: "High — simpler than 4-HO-MET (one fewer step).",
      solventsUsed: ["THF", "Ethyl Acetate", "Ethanol", "Heptane"],
      equipment: [
        "100 L jacketed glass-lined reactor",
        "LAH addition port + N₂ inerting",
        "Scrubbed vent (for step 1 HCl)",
        "Crystallization tank + wash station"
      ],
      batchPlan: {
        targetBatch: "1.0 kg 4-AcO-DET fumarate",
        startingMaterial: "1.9 kg 4-acetoxyindole (≈ 10.8 mol)",
        keyVolumes: [
          "THF: 14 L",
          "Oxalyl chloride: 1.05 kg",
          "Diethylamine: 1.6 kg",
          "LAH: 1.6 kg",
          "Ethanol (crystallization): 10 L",
          "Heptane (wash): 4 L"
        ]
      },
      hazards: [
        "Identical LAH + oxalyl chloride hazards as 4-HO-MET",
        "Diethylamine is a strong irritant + flammable (flash pt −28 °C) — closed-loop handling"
      ],
      vendorNotes: ["Same as 4-HO-MET; diethylamine via Sigma, Thermo, Matrix"],
      estCostPerKg: "$14,000 – $19,000"
    },
    safety: {
      acuteToxicity: "No published LD50. Human recreational doses 15–30 mg oral (prodrug). No fatalities in published literature.",
      chronicConcerns: ["5-HT2B activity — chronic cardiac monitoring", "Serotonin syndrome potential with serotonergic polypharmacy"],
      contraindications: ["Psychotic-spectrum history", "MAOI use", "Structural cardiac disease"],
      acuteSideEffects: ["Visual/perceptual alteration", "Transient nausea during onset", "Tachycardia, mild HTN", "Mydriasis"],
      ehsClassification: "GHS Cat. 4 oral (suspected); lab-coat/gloves/goggles; no respirator required for solid handling."
    },
    publishedResearch: [
      { citation: "Klein AK et al. ACS Pharmacol Transl Sci. 2021;4(2):533-542.", pmid: "33860181", pmc: "PMC8033608", url: "https://pmc.ncbi.nlm.nih.gov/articles/PMC8033608/" },
      { citation: "Leuner H, Baer G. Two new short acting hallucinogens of the psilocybin type (CZ-74 / CEY-19). Neuro-psychopharmacology. 1965;4:471-474." },
      { citation: "Nichols DE, Frescas S. Improvements to the synthesis of psilocybin and a facile method for preparing the O-acetyl prodrug of psilocin. Synthesis. 1999;935-938." },
      { citation: "Hofmann A. U.S. Patent 3,075,992 — Esters of indoles (1963, expired)." }
    ],
    patentInfo: "Hofmann 1963 patent expired. Composition space is public domain. Formulation/crystal-form/method-of-use patents available. Current psychedelic-pharma companies (Mindstate, Cybin) have been filing around N,N-diethyl scaffolds.",
    wohlerScoring: {
      methodology: "Wohler Scoring System v1.0",
      benefitBreakdown: { therapeuticRationale: 22, evidenceQuality: 19, unmetMedicalNeed: 18, novelty: 12, universityInterest: 14 },
      harmBreakdown: { acuteToxicity: 5, chronicToxicity: 7, dependence: 3, schedulingRisk: 9 },
      netValue: 61,
      confidenceRationale: "HIGH — long human clinical history via CZ-74; modern SAR data published; scalable synthesis."
    },
    priorityTier: "Tier 1",
    nextSteps: ["Reference-standard production for CROs", "IRB dossier emphasizing 60-yr human safety record", "Patent filing on crystal form"]
  },

  "4-aco-dpt": {
    id: "4-aco-dpt",
    name: "4-AcO-DPT",
    fullName: "4-Acetoxy-N,N-dipropyltryptamine",
    aliases: ["Procetin"],
    categoryId: "tryptamines",
    program: "Program B",
    cas: "1024612-25-6",
    formula: "C₁₈H₂₆N₂O₂",
    molecularWeight: "302.41 g/mol",
    schedulingStatus: "unscheduled",
    schedulingNote: "Not federally scheduled. Structural relative of psilocin; same analog considerations as 4-HO-MET. DPT (N,N-dipropyltryptamine, non-4-hydroxy) is also unscheduled federally.",
    benefitScore: 89,
    harmScore: 23,
    evidenceConfidence: "HIGH",
    status: "Strongly Favored",
    statusColor: "green",
    goDecision: "GO — lead differentiation candidate",
    summary: "Prodrug for 4-HO-DPT. The distinguishing pharmacological feature: 129-fold 5-HT2A/5-HT2C selectivity vs. ≈1-fold for psilocin itself. 5-HT2C activation is implicated in anxiety, appetite suppression, and adverse subjective effects — a truly 5-HT2A-selective psychedelic is a genuinely novel therapeutic concept. EC₅₀ ≈ 23.7 nM at 5-HT2A with near-full agonist efficacy.",
    pros: [
      "129-fold 5-HT2A/5-HT2C selectivity — genuinely novel SAR differentiation vs. psilocybin",
      "Near-full 5-HT2A agonist efficacy (Emax ≈ 96 %)",
      "Theoretical therapeutic advantage: cleaner psychedelic experience with less anxiety/nausea",
      "Publishable, patentable differentiation story",
      "Same Speeter-Anthony synthesis infrastructure as 4-AcO-MET"
    ],
    cons: [
      "No human clinical data — novel compound",
      "5-HT2B still active — cardiac concern for chronic dosing",
      "Scheduling scrutiny (psilocin scaffold)"
    ],
    pharmacology: {
      targets: [
        { receptor: "5-HT2A", activity: "Near-full agonist", value: "EC₅₀ ≈ 23.7 nM; Emax ≈ 96 %" },
        { receptor: "5-HT2C", activity: "Very weak partial agonist", value: "129-fold less potent than 5-HT2A" },
        { receptor: "5-HT2B", activity: "Partial agonist", value: "Emax moderate" }
      ],
      htrED50: "~1.0 mg/kg mouse (preliminary)",
      duration: "Estimated 5–6 h",
      halfLife: "Prodrug cleaved to 4-HO-DPT rapidly in plasma",
      metabolism: "Esterase + CYP N-depropylation",
      mechanism: "5-HT2A-selective psychedelic; potentially cleaner neuroplasticity induction without 5-HT2C-mediated adverse effects"
    },
    labSynthesis: {
      title: "Laboratory-Scale Synthesis",
      overview: "Speeter-Anthony from 4-acetoxyindole with N,N-dipropylamine in amidation step.",
      steps: [
        { step: "1", title: "Glyoxalyl chloride", description: "4-Acetoxyindole + oxalyl chloride in THF, 0 °C, 30 min.", solvent: "THF", keyReagents: "Oxalyl chloride" },
        { step: "2", title: "Amidation", description: "Dipropylamine (2.5 eq) + Et₃N, 0 °C → RT, 2 h.", solvent: "THF", keyReagents: "Dipropylamine, Et₃N" },
        { step: "3", title: "LAH reduction", description: "LAH (4 eq), reflux 4 h.", solvent: "THF", keyReagents: "LAH" },
        { step: "4", title: "Fumarate formation", description: "EtOH + fumaric acid; crystallize from EtOH/heptane.", solvent: "EtOH/Heptane", keyReagents: "Fumaric acid" }
      ],
      yield: "45–55 %",
      purityTarget: "≥ 99.0 % HPLC"
    },
    kgScaleSynthesis: {
      title: "Kilogram-Scale Plan",
      feasibility: "High — identical infrastructure to 4-AcO-DET.",
      solventsUsed: ["THF", "Ethyl Acetate", "Ethanol", "Heptane"],
      equipment: ["Same as 4-AcO-DET"],
      batchPlan: {
        targetBatch: "1.0 kg 4-AcO-DPT fumarate",
        startingMaterial: "1.7 kg 4-acetoxyindole (≈ 9.7 mol)",
        keyVolumes: [
          "THF: 14 L",
          "Oxalyl chloride: 0.94 kg",
          "Dipropylamine: 2.4 kg",
          "LAH: 1.45 kg",
          "Ethanol: 10 L"
        ]
      },
      hazards: ["Same LAH/oxalyl chloride hazards as siblings; dipropylamine moderately flammable"],
      vendorNotes: ["Dipropylamine: Sigma, TCI, Thermo"],
      estCostPerKg: "$15,500 – $21,000"
    },
    safety: {
      acuteToxicity: "No published human LD50. Preclinical HTR profile suggests dose range ≈ 2× psilocin in humans.",
      chronicConcerns: ["5-HT2B activity; cardiac monitoring", "No chronic dosing data available"],
      contraindications: ["Psychosis spectrum", "MAOIs", "Cardiac disease"],
      acuteSideEffects: ["Psychedelic effects — visual, perceptual, mood", "Transient nausea", "Tachycardia", "Mydriasis"],
      ehsClassification: "GHS Cat. 4 oral (suspected)"
    },
    publishedResearch: [
      { citation: "Klein AK et al. ACS Pharmacol Transl Sci. 2021;4(2):533-542.", pmc: "PMC8033608", url: "https://pmc.ncbi.nlm.nih.gov/articles/PMC8033608/" },
      { citation: "Halberstadt AL, Chatha M, Klein AK, Wallach J, Brandt SD. Correlation between the potency of hallucinogens in the mouse head-twitch response assay and their behavioral and subjective effects in humans. Neuropharmacology. 2020;167:107933." }
    ],
    patentInfo: "Composition space effectively open. Strong patent candidate for (a) 4-AcO-DPT crystal form, (b) 5-HT2A-selective psychedelic therapy method-of-use, (c) synthesis improvements.",
    wohlerScoring: {
      methodology: "Wohler Scoring System v1.0",
      benefitBreakdown: { therapeuticRationale: 22, evidenceQuality: 19, unmetMedicalNeed: 18, novelty: 18, universityInterest: 12 },
      harmBreakdown: { acuteToxicity: 5, chronicToxicity: 7, dependence: 3, schedulingRisk: 8 },
      netValue: 66,
      confidenceRationale: "HIGH — published SAR data; distinct receptor profile is the differentiation story."
    },
    priorityTier: "Tier 1 — Lead novel compound",
    nextSteps: ["Publish receptor selectivity paper in collaboration with McCorvy lab", "Patent crystal form + method-of-use", "Generate reference standard"]
  },

  "4-aco-ept": {
    id: "4-aco-ept",
    name: "4-AcO-EPT",
    fullName: "4-Acetoxy-N-ethyl-N-propyltryptamine",
    aliases: [],
    categoryId: "tryptamines",
    program: "Program B",
    cas: "N/A (not yet assigned)",
    formula: "C₁₇H₂₄N₂O₂",
    molecularWeight: "288.39 g/mol",
    schedulingStatus: "unscheduled",
    schedulingNote: "Not federally scheduled. Psilocin analog considerations apply.",
    benefitScore: 84,
    harmScore: 25,
    evidenceConfidence: "MODERATE",
    status: "Favored",
    statusColor: "green",
    goDecision: "GO — high potency + 5-HT2B plasticity interest",
    summary: "Prodrug for 4-HO-EPT. Notably, 4-HO-EPT is among the most potent psilocin analogs at 5-HT2A (EC₅₀ ≈ 3.15 nM). The asymmetric N-ethyl-N-propyl substitution produces high 5-HT2B efficacy (89 %) — of interest for neuroplasticity research (5-HT2B contributes to BDNF release).",
    pros: [
      "Among the most potent 5-HT2A agonists in the psilocin family (EC₅₀ ≈ 3.15 nM)",
      "High 5-HT2B efficacy (89 %) — relevant for neuroplasticity research",
      "Asymmetric N-alkyl substitution — structurally distinct from symmetric tryptamines",
      "Same synthesis route as 4-AcO-MET"
    ],
    cons: [
      "5-HT2B activity — cardiac caution",
      "Minimal human data",
      "Psilocin scheduling concern"
    ],
    pharmacology: {
      targets: [
        { receptor: "5-HT2A", activity: "Near-full agonist", value: "EC₅₀ ≈ 3.15 nM" },
        { receptor: "5-HT2B", activity: "Near-full agonist", value: "Emax ≈ 89 %" },
        { receptor: "5-HT2C", activity: "Partial agonist" }
      ],
      htrED50: "0.51 mg/kg mouse",
      duration: "Estimated 5 h",
      metabolism: "Esterase + CYP",
      mechanism: "5-HT2A-driven psychedelic + enhanced 5-HT2B neuroplasticity component"
    },
    labSynthesis: {
      title: "Laboratory-Scale Synthesis",
      overview: "Same Speeter-Anthony scaffold; N-ethyl-N-propylamine in amidation.",
      steps: [
        { step: "1", title: "Glyoxalyl chloride", description: "4-Acetoxyindole + oxalyl chloride, THF, 0 °C.", solvent: "THF", keyReagents: "Oxalyl chloride" },
        { step: "2", title: "Amidation", description: "N-ethyl-N-propylamine (2.5 eq) + Et₃N, RT, 2 h.", solvent: "THF", keyReagents: "EtNHPr, Et₃N" },
        { step: "3", title: "LAH reduction", description: "LAH (4 eq) in THF, reflux.", solvent: "THF", keyReagents: "LAH" },
        { step: "4", title: "Fumarate salt", description: "EtOH + fumaric acid; crystallize.", solvent: "EtOH", keyReagents: "Fumaric acid" }
      ],
      yield: "~45 %",
      purityTarget: "≥ 99.0 % HPLC"
    },
    kgScaleSynthesis: {
      title: "Kilogram-Scale Plan",
      feasibility: "High",
      solventsUsed: ["THF", "Ethanol", "Ethyl Acetate", "Heptane"],
      equipment: ["Same as siblings"],
      batchPlan: {
        targetBatch: "1.0 kg",
        startingMaterial: "1.8 kg 4-acetoxyindole",
        keyVolumes: ["THF: 14 L", "N-ethyl-N-propylamine: 2.0 kg", "LAH: 1.5 kg", "EtOH: 10 L"]
      },
      hazards: ["Same as 4-AcO family"],
      vendorNotes: ["N-ethyl-N-propylamine: TCI, Matrix"],
      estCostPerKg: "$16,000 – $22,000"
    },
    safety: {
      acuteToxicity: "No published LD50",
      chronicConcerns: ["5-HT2B efficacy 89 % — highest cardiac concern of 4-AcO series; chronic dosing protocols require echocardiography"],
      contraindications: ["Valvular disease", "MAOIs", "Psychosis history"],
      acuteSideEffects: ["Psychedelic effects", "Tachycardia", "Possible hypertension"],
      ehsClassification: "GHS Cat. 4 oral (suspected)"
    },
    publishedResearch: [
      { citation: "Klein AK et al. ACS Pharmacol Transl Sci. 2021;4(2):533-542.", pmc: "PMC8033608", url: "https://pmc.ncbi.nlm.nih.gov/articles/PMC8033608/" }
    ],
    patentInfo: "Open composition space. Potential composition-of-matter filing opportunity for asymmetric N-alkyl tryptamines.",
    wohlerScoring: {
      methodology: "Wohler Scoring System v1.0",
      benefitBreakdown: { therapeuticRationale: 21, evidenceQuality: 16, unmetMedicalNeed: 17, novelty: 16, universityInterest: 14 },
      harmBreakdown: { acuteToxicity: 5, chronicToxicity: 10, dependence: 3, schedulingRisk: 7 },
      netValue: 59,
      confidenceRationale: "MODERATE — potent in vitro but limited in vivo/human data; 5-HT2B concern elevated."
    },
    priorityTier: "Tier 2",
    nextSteps: ["5-HT2B specificity counter-screen", "Cardiac safety dossier before CRO distribution"]
  },

  "4-aco-mpt": {
    id: "4-aco-mpt",
    name: "4-AcO-MPT",
    fullName: "4-Acetoxy-N-methyl-N-propyltryptamine",
    aliases: [],
    categoryId: "tryptamines",
    program: "Program B",
    formula: "C₁₆H₂₂N₂O₂",
    molecularWeight: "274.36 g/mol",
    schedulingStatus: "unscheduled",
    schedulingNote: "Not federally scheduled.",
    benefitScore: 78,
    harmScore: 25,
    evidenceConfidence: "MODERATE",
    status: "Favored",
    statusColor: "green",
    goDecision: "GO — intermediate SAR member",
    summary: "Prodrug for 4-HO-MPT. ED50 = 0.55 mg/kg in mouse HTR — potency between 4-AcO-MET and 4-AcO-DET. Near-full 5-HT2A agonist (Emax 96 %). Useful for SAR studies bridging methyl/ethyl and propyl groups.",
    pros: [
      "Intermediate SAR data point in methyl/ethyl/propyl tryptamine series",
      "ED50 = 0.55 mg/kg — strong in vivo activity",
      "Asymmetric N-alkyl = distinct PK from symmetric analogs",
      "Same manufacturing infrastructure"
    ],
    cons: ["Less differentiated than 4-AcO-DPT", "Limited human data", "Psilocin scheduling scrutiny"],
    pharmacology: {
      targets: [
        { receptor: "5-HT2A", activity: "Near-full agonist", value: "Emax ≈ 96 %" },
        { receptor: "5-HT2B", activity: "Partial agonist" },
        { receptor: "5-HT2C", activity: "Partial agonist" }
      ],
      htrED50: "0.55 mg/kg mouse",
      duration: "Estimated 4–6 h",
      mechanism: "5-HT2A psychedelic"
    },
    labSynthesis: {
      title: "Laboratory-Scale Synthesis",
      overview: "Speeter-Anthony with N-methyl-N-propylamine.",
      steps: [
        { step: "1", title: "Glyoxalyl chloride", description: "4-Acetoxyindole + oxalyl chloride.", solvent: "THF", keyReagents: "Oxalyl chloride" },
        { step: "2", title: "Amidation", description: "N-methyl-N-propylamine + Et₃N.", solvent: "THF", keyReagents: "MePrN-H" },
        { step: "3", title: "LAH reduction", description: "Standard LAH.", solvent: "THF", keyReagents: "LAH" },
        { step: "4", title: "Fumarate salt", description: "EtOH crystallization.", solvent: "EtOH", keyReagents: "Fumaric acid" }
      ],
      yield: "~48 %",
      purityTarget: "≥ 99.0 % HPLC"
    },
    kgScaleSynthesis: {
      title: "Kilogram-Scale Plan",
      feasibility: "High",
      solventsUsed: ["THF", "EtOH", "EtOAc", "Heptane"],
      equipment: ["Same as siblings"],
      batchPlan: { targetBatch: "1.0 kg", startingMaterial: "1.9 kg 4-acetoxyindole", keyVolumes: ["THF: 14 L", "N-methyl-N-propylamine: 1.9 kg", "LAH: 1.55 kg", "EtOH: 10 L"] },
      hazards: ["Same as 4-AcO family"],
      vendorNotes: ["N-methyl-N-propylamine: TCI, Sigma"],
      estCostPerKg: "$15,000 – $20,000"
    },
    safety: {
      acuteToxicity: "No published LD50",
      chronicConcerns: ["5-HT2B class effect"],
      contraindications: ["Psychosis, MAOIs, cardiac"],
      acuteSideEffects: ["Psychedelic class effects"],
      ehsClassification: "GHS Cat. 4 oral (suspected)"
    },
    publishedResearch: [
      { citation: "Klein AK et al. ACS Pharmacol Transl Sci. 2021;4(2):533-542.", pmc: "PMC8033608", url: "https://pmc.ncbi.nlm.nih.gov/articles/PMC8033608/" }
    ],
    patentInfo: "Open composition space.",
    wohlerScoring: {
      methodology: "Wohler Scoring System v1.0",
      benefitBreakdown: { therapeuticRationale: 19, evidenceQuality: 16, unmetMedicalNeed: 16, novelty: 12, universityInterest: 15 },
      harmBreakdown: { acuteToxicity: 5, chronicToxicity: 8, dependence: 3, schedulingRisk: 9 },
      netValue: 53,
      confidenceRationale: "MODERATE — SAR value but less differentiated"
    },
    priorityTier: "Tier 2",
    nextSteps: ["SAR comparison batch with 4-AcO-MET and 4-AcO-DET", "CRO reference standard"]
  },

  "4-ho-mipt": {
    id: "4-ho-mipt",
    name: "4-HO-MiPT",
    fullName: "4-Hydroxy-N-methyl-N-isopropyltryptamine",
    aliases: ["Miprocin"],
    categoryId: "tryptamines",
    program: "Program B",
    cas: "77872-41-4",
    formula: "C₁₄H₂₀N₂O",
    molecularWeight: "232.32 g/mol",
    schedulingStatus: "unscheduled",
    schedulingNote: "Not federally scheduled. Note: 5-MeO-MiPT was federally scheduled in 2022; the 4-OH isomer remains distinct and unscheduled.",
    benefitScore: 82,
    harmScore: 24,
    evidenceConfidence: "MODERATE",
    status: "Favored",
    statusColor: "green",
    goDecision: "GO — longer-duration option",
    summary: "4-HO-MiPT (Miprocin) is a direct 4-hydroxytryptamine (not a prodrug). Near-full 5-HT2A agonist (EC₅₀ ≈ 5.2 nM, Emax ≈ 99.6 %). Human experiential literature reports a gentler onset and longer duration than psilocin (5–6 h) — likely due to isopropyl group effect on metabolism.",
    pros: [
      "Near-full 5-HT2A agonist — psilocin-comparable receptor profile",
      "Longer duration (5–6 h) — attractive for extended structured sessions",
      "Gentler onset curve reported in phenomenology literature",
      "Direct 4-OH compound — no prodrug conversion variability"
    ],
    cons: [
      "5-MeO-MiPT scheduled in 2022 — potential scheduling trajectory for N-isopropyl class",
      "Limited rigorous clinical data",
      "Duration may exceed optimal therapy-session window"
    ],
    pharmacology: {
      targets: [
        { receptor: "5-HT2A", activity: "Near-full agonist", value: "EC₅₀ ≈ 5.20 nM; Emax ≈ 99.6 %" },
        { receptor: "5-HT2B", activity: "Partial agonist" },
        { receptor: "5-HT2C", activity: "Partial agonist" }
      ],
      htrED50: "Active in low-mg/kg range",
      duration: "5–6 h oral",
      halfLife: "Estimated 2–3 h",
      metabolism: "Glucuronidation + CYP2D6 N-dealkylation",
      mechanism: "5-HT2A psychedelic"
    },
    labSynthesis: {
      title: "Laboratory-Scale Synthesis",
      overview: "Speeter-Anthony from 4-acetoxyindole with N-methyl-N-isopropylamine, followed by O-deacetylation (same as 4-HO-MET).",
      steps: [
        { step: "1", title: "Glyoxalyl chloride", description: "4-Acetoxyindole + oxalyl chloride, THF, 0 °C.", solvent: "THF", keyReagents: "Oxalyl chloride" },
        { step: "2", title: "Amidation", description: "N-methyl-N-isopropylamine (2.5 eq) + Et₃N.", solvent: "THF", keyReagents: "MeiPrN-H" },
        { step: "3", title: "LAH reduction", description: "LAH reflux 4 h.", solvent: "THF", keyReagents: "LAH" },
        { step: "4", title: "O-deacetylation", description: "K₂CO₃/MeOH, RT, 2 h.", solvent: "MeOH", keyReagents: "K₂CO₃" },
        { step: "5", title: "Fumarate crystallization", description: "EtOH/heptane.", solvent: "EtOH/Heptane", keyReagents: "Fumaric acid" }
      ],
      yield: "40–50 %",
      purityTarget: "≥ 99.0 %"
    },
    kgScaleSynthesis: {
      title: "Kilogram-Scale Plan",
      feasibility: "High",
      solventsUsed: ["THF", "Methanol", "Ethanol", "Ethyl Acetate", "Heptane"],
      equipment: ["Standard LAH-rated reactor + scrubber"],
      batchPlan: { targetBatch: "1.0 kg Miprocin fumarate", startingMaterial: "2.2 kg 4-acetoxyindole", keyVolumes: ["THF: 16 L", "MeOH: 12 L", "EtOH: 10 L"] },
      hazards: ["LAH + oxalyl chloride"],
      vendorNotes: ["N-methyl-N-isopropylamine: Sigma, TCI"],
      estCostPerKg: "$19,000 – $25,000"
    },
    safety: {
      acuteToxicity: "No LD50 published",
      chronicConcerns: ["5-HT2B class effect"],
      contraindications: ["Psychosis, MAOI, cardiac"],
      acuteSideEffects: ["Psychedelic class", "Tachycardia"],
      ehsClassification: "GHS Cat. 4 oral (suspected)"
    },
    publishedResearch: [
      { citation: "Klein AK et al. ACS Pharmacol Transl Sci. 2021;4(2):533-542.", pmc: "PMC8033608", url: "https://pmc.ncbi.nlm.nih.gov/articles/PMC8033608/" },
      { citation: "Shulgin A, Shulgin A. TIHKAL: The Continuation. Transform Press; 1997." }
    ],
    patentInfo: "Open composition space.",
    wohlerScoring: {
      methodology: "Wohler Scoring System v1.0",
      benefitBreakdown: { therapeuticRationale: 20, evidenceQuality: 17, unmetMedicalNeed: 17, novelty: 14, universityInterest: 14 },
      harmBreakdown: { acuteToxicity: 5, chronicToxicity: 7, dependence: 3, schedulingRisk: 9 },
      netValue: 58,
      confidenceRationale: "MODERATE"
    },
    priorityTier: "Tier 2",
    nextSteps: ["Duration-of-action PK study proposal", "SAR comparison with 4-AcO-MET"]
  },

  "5-meo-nmt": {
    id: "5-meo-nmt",
    name: "5-MeO-NMT",
    fullName: "5-Methoxy-N-methyltryptamine",
    aliases: [],
    categoryId: "tryptamines",
    program: "Program B",
    formula: "C₁₂H₁₆N₂O",
    molecularWeight: "204.27 g/mol",
    schedulingStatus: "unscheduled",
    schedulingNote: "Not federally scheduled. 5-MeO-DMT is Schedule I; 5-MeO-NMT (monomethyl) is distinct.",
    benefitScore: 70,
    harmScore: 28,
    evidenceConfidence: "MODERATE",
    status: "Favored",
    statusColor: "green",
    goDecision: "GO — non-psychedelic-spectrum research tool",
    summary: "Naturally occurring 5-methoxytryptamine — found in several plant species. Partial 5-HT1A/5-HT2A agonist with substantially attenuated psychedelic intensity vs. 5-MeO-DMT. A Nature 2024 structural pharmacology paper highlights 5-methoxytryptamines as a class with broad therapeutic potential.",
    pros: [
      "Naturally occurring — strong 'known plant alkaloid' positioning",
      "Attenuated intensity vs. 5-MeO-DMT — easier clinical use",
      "Recent Nature 2024 paper highlights class therapeutic potential",
      "Simple synthesis from 5-methoxyindole"
    ],
    cons: ["Weaker pharmacology → smaller therapeutic signal", "Less psychedelic research interest than 4-OH series"],
    pharmacology: {
      targets: [
        { receptor: "5-HT1A", activity: "Partial agonist", value: "Moderate affinity" },
        { receptor: "5-HT2A", activity: "Partial agonist", value: "Lower than 5-MeO-DMT" }
      ],
      duration: "Estimated 2–3 h",
      mechanism: "Moderate serotonergic agonism — anxiolytic profile at low dose, psychedelic at high dose"
    },
    labSynthesis: {
      title: "Laboratory-Scale Synthesis",
      overview: "Speeter-Anthony from 5-methoxyindole + methylamine.",
      steps: [
        { step: "1", title: "Glyoxalyl chloride", description: "5-Methoxyindole + oxalyl chloride, THF.", solvent: "THF", keyReagents: "Oxalyl chloride" },
        { step: "2", title: "Amidation", description: "Methylamine (2.5 eq) + Et₃N.", solvent: "THF", keyReagents: "MeNH₂" },
        { step: "3", title: "LAH reduction", description: "LAH reflux 4 h.", solvent: "THF", keyReagents: "LAH" },
        { step: "4", title: "HCl salt", description: "HCl/EtOAc; crystallize.", solvent: "EtOAc", keyReagents: "HCl" }
      ],
      yield: "55–65 %",
      purityTarget: "≥ 99.0 %"
    },
    kgScaleSynthesis: {
      title: "Kilogram-Scale Plan",
      feasibility: "High — simpler than 4-OH series (no deacetylation)",
      solventsUsed: ["THF", "EtOAc", "Ethanol"],
      equipment: ["Standard"],
      batchPlan: { targetBatch: "1.0 kg 5-MeO-NMT·HCl", startingMaterial: "1.4 kg 5-methoxyindole", keyVolumes: ["THF: 12 L", "EtOAc: 10 L"] },
      hazards: ["Methylamine gas — closed handling"],
      vendorNotes: ["5-Methoxyindole: Sigma, Matrix, Combi-Blocks"],
      estCostPerKg: "$9,500 – $13,000"
    },
    safety: {
      acuteToxicity: "No published LD50 specific to 5-MeO-NMT",
      chronicConcerns: ["Serotonin syndrome with MAOI/SSRI"],
      contraindications: ["MAOI therapy", "Serotonin syndrome risk"],
      acuteSideEffects: ["Mild anxiolytic/sedative", "At high doses mild psychedelic"],
      ehsClassification: "GHS Cat. 4 oral (suspected)"
    },
    publishedResearch: [
      { citation: "Structural pharmacology and therapeutic potential of 5-methoxytryptamines. Nature. 2024.", pmc: "PMC11152992" }
    ],
    patentInfo: "Composition-of-matter limited (known natural product); method-of-use and formulation patents available.",
    wohlerScoring: {
      methodology: "Wohler Scoring System v1.0",
      benefitBreakdown: { therapeuticRationale: 16, evidenceQuality: 14, unmetMedicalNeed: 14, novelty: 12, universityInterest: 14 },
      harmBreakdown: { acuteToxicity: 6, chronicToxicity: 6, dependence: 4, schedulingRisk: 12 },
      netValue: 42,
      confidenceRationale: "MODERATE"
    },
    priorityTier: "Tier 3",
    nextSteps: ["Literature-review expansion", "5-HT1A vs 5-HT2A ratio characterization"]
  }
};